/*
 * Copyright (C) 2024 Webformat S.r.l.
 * https://www.webformat.com
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {Action, getModule, Module, MutationAction, VuexModule} from 'vuex-module-decorators';
import store from '@/store';
import {LoyaltyBenefit} from '@/store/models';
import checkout from '@/store/modules/checkout';
import cart from '@/store/modules/cart';
import {reloadCustomerBenefits, resetQuoteBenefits, updateQuoteBenefits} from '@/store/api';
import handleError from '@/commons/errorHandler';
import {i18n} from '@/i18n';
import to from 'await-to-js';
import handleMessage from '@/commons/messageManager';
import {initState} from '../stateinit';

@Module({
    namespaced: true,
    name: 'loyalty',
    store,
    dynamic: true,
})
class LoyaltyModule extends VuexModule {
    public loyalty: any = initState('loyalty');
    public loyaltyBenefits: any = initState('loyalty_benefits');

    get productPurchasePoints() {
        return this.loyalty.product_purchase || 0;
    }

    get inAppProductPurchasePoints() {
        return this.loyalty.in_app_product_purchase || 0;
    }

    get isSubscribed() {
        return this.loyalty.is_subscribed;
    }

    get customerBenefits(): LoyaltyBenefit[] {
        return Object.values(this.loyaltyBenefits.benefits || {});
    }

    get loyaltyBenefitsSkip(): number {
        return this.customerBenefits.findIndex((benefit) => {
            return benefit.benefit_type === 'skip_queue';
        });
    }

    @Action
    public async updateQuoteBenefit(benefit: LoyaltyBenefit) {
        cart.flagLoading({key: 'totals', value: true});
        const benefitType: any = {};
        benefitType[benefit.benefit_type] = benefit.is_selected ? 1 : 0;

        if (benefit.benefit_type === 'skip_queue') {
            await checkout.changeSelectedShippingOption({
                code: 'skip',
                newStatus: Boolean(benefitType[benefit.benefit_type]),
            });
        }

        cart.cart.extension_attributes.benefits = JSON.stringify(
            Object.assign(JSON.parse(cart.cart.extension_attributes.benefits || '{}'), benefitType)
        );

        await updateQuoteBenefits(cart.cart.extension_attributes.benefits).then(async (result) => {
            if (result) {
                await cart.getTotals();
                await checkout.estimateShipping(checkout.shippingAddress);
                await checkout.checkSingleShippingMethod();
                await checkout.getPaymentMethods(
                    checkout.billingSameAsShipping ? checkout.shippingAddress : checkout.billingAddress,
                    checkout.shippingAddress
                );
            } else {
                handleError(i18n.t(`Sorry, we can't apply this service right now`).toString());
                await this.resetQuoteBenefits();
            }
        });

        cart.flagLoading({key: 'totals', value: false});
    }

    @Action
    public async resetQuoteBenefits() {
        cart.flagLoading({key: 'totals', value: true});

        const hasActiveBenefits = this.customerBenefits.some((benefit: LoyaltyBenefit) => {
            return benefit.is_selected;
        });

        if (hasActiveBenefits) {
            await resetQuoteBenefits().then(async (result) => {
                for (const removeBenefit of this.customerBenefits) {
                    if (removeBenefit.benefit_type === 'skip_queue' && removeBenefit.is_selected) {
                        // Remove shipping option SKIP if selected
                        // eslint-disable-next-line no-await-in-loop
                        await checkout.changeSelectedShippingOption({
                            code: 'skip',
                            newStatus: false,
                        });
                    }
                    removeBenefit.is_selected = false;
                }

                cart.cart.extension_attributes.benefits = null;

                if (result) {
                    await cart.getTotals();
                    await checkout.estimateShipping(checkout.shippingAddress);
                    await checkout.checkSingleShippingMethod();
                    await checkout.getPaymentMethods(
                        checkout.billingSameAsShipping ? checkout.shippingAddress : checkout.billingAddress,
                        checkout.shippingAddress
                    );
                } else {
                    handleError(i18n.t(`Sorry, we can't apply this service right now`).toString());
                }

                handleMessage('error', i18n.t(`Benefits have been reset, try to apply again`).toString());
            });

            await this.reloadLoyaltyBenefits(this.isSubscribed).then(() => {
                this.customerBenefits.forEach((benefit: LoyaltyBenefit) => {
                    if (benefit.is_selected) {
                        const benefitType: any = {};
                        benefitType[benefit.benefit_type] = 1;

                        cart.cart.extension_attributes.benefits = JSON.stringify(
                            Object.assign(JSON.parse(cart.cart.extension_attributes.benefits || '{}'), benefitType)
                        );
                    }
                });
            });
        }

        cart.flagLoading({key: 'totals', value: false});
    }

    @MutationAction({ mutate: ['loyaltyBenefits'] })
    public async reloadLoyaltyBenefits(isSubscribed: boolean = true) {
        cart.flagLoading({ key: 'totals', value: true });

        let result;

        if (isSubscribed) {
            [, result] = await to(reloadCustomerBenefits());
        } else {
            result = [[]];
        }

        cart.flagLoading({ key: 'totals', value: false });
        return { loyaltyBenefits: { benefits: result[0] } };
    }
}

export default getModule(LoyaltyModule);
